/**
 * Anatomy Slider
 *
 * Figma: 03_BPH Education Section A.
 *
 * Two behaviours, not one. The illustration travels — it is a rail of
 * diagrams that slides, and that is the part you can drag. The copy beside it
 * does not travel; it cross-fades in place. Dragging the whole row moved the
 * text sideways with the picture, which read as one wide thing being shoved
 * rather than a diagram being stepped through.
 *
 * That split is why this rebuilds the DOM on init. The markup ships as
 * complete slides — diagram and copy together — because that is what makes
 * sense to edit and what reads correctly with no JavaScript. A rail needs the
 * diagrams to be siblings, so they are lifted out into one, the copy into a
 * stack beside it, and the now-empty slide wrappers dropped. `view.js` is
 * front-end only, so the editor keeps the honest structure.
 */

/** Fraction of the rail a drag must cover before it counts as a change. */
const DRAG_THRESHOLD = 0.15;

/** Pointer travel, in px, past which a release is a drag and not a click. */
const CLICK_SLOP = 5;

const NS = 'wp-block-embark-anatomy-slider';
const SLIDE = 'wp-block-embark-anatomy-slide';

class AnatomySlider {
	current = 0;

	dragging = false;

	moved = 0;

	constructor(el) {
		this.el = el;
		this.slides = [...el.querySelectorAll(`.${SLIDE}`)];

		if (this.slides.length < 2) {
			return;
		}

		this.build();
		this.el.classList.add('is-slider');
		this.bindEvents();
		this.bindDrag();
		this.show(0, false);

		window.addEventListener('resize', () => this.show(this.current, false));
	}

	/**
	 * Lift the diagrams into a rail and the copy into a stack.
	 */
	build = () => {
		const track = this.el.querySelector(`.${NS}__track`);

		this.stage = document.createElement('div');
		this.stage.className = `${NS}__stage`;

		this.rail = document.createElement('div');
		this.rail.className = `${NS}__rail`;

		const clip = document.createElement('div');
		clip.className = `${NS}__rail-clip`;
		clip.append(this.rail);

		this.panels = document.createElement('div');
		this.panels.className = `${NS}__panels`;

		// The first slide's own controls are reused rather than rebuilt, so
		// the markup stays the source of the arrows and their labels.
		this.dots = this.slides[0].querySelector('[data-anatomy-dots]');
		this.arrows = this.slides[0].querySelector('[data-anatomy-arrows]');

		this.panelEls = this.slides.map((slide) => {
			const diagram = slide.querySelector(`.${SLIDE}__diagram`);
			const copy = slide.querySelector(`.${SLIDE}__copy`);

			if (diagram) {
				this.rail.append(diagram);
			}
			if (copy) {
				copy.classList.add(`${NS}__panel`);
				this.panels.append(copy);
			}

			return copy;
		});

		const media = document.createElement('div');
		media.className = `${NS}__col-media`;
		media.append(clip);
		if (this.dots) {
			media.append(this.dots);
		}

		const content = document.createElement('div');
		content.className = `${NS}__col-content`;
		content.append(this.panels);
		if (this.arrows) {
			content.append(this.arrows);
		}

		this.stage.append(media, content);
		track.replaceChildren(this.stage);

		this.buildDots();
	};

	buildDots = () => {
		if (!this.dots) {
			return;
		}

		this.dots.replaceChildren();

		this.slides.forEach((_, i) => {
			const dot = document.createElement('button');
			dot.type = 'button';
			dot.className = `${SLIDE}__dot`;
			dot.dataset.anatomyGo = String(i);
			dot.innerHTML = `<span class="screen-reader-text">${i + 1}</span>`;
			this.dots.append(dot);
		});
	};

	bindEvents = () => {
		this.el.addEventListener('click', (e) => {
			// A drag that ends over a control must not also press it.
			if (this.moved > CLICK_SLOP) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}

			const go = e.target.closest('[data-anatomy-go]');
			if (go) {
				this.show(Number(go.dataset.anatomyGo));
				return;
			}

			if (e.target.closest('[data-anatomy-prev]')) {
				this.show(this.current - 1);
			}

			if (e.target.closest('[data-anatomy-next]')) {
				this.show(this.current + 1);
			}
		});
	};

	/**
	 * Drag lives on the rail alone — the copy is read, not handled.
	 */
	bindDrag = () => {
		/**
		 * Stop the browser starting a native image drag.
		 *
		 * Left alone, pressing on the illustration and moving picks the
		 * picture up — a translucent copy follows the cursor and the rail
		 * never sees the gesture. `-webkit-user-drag` in the stylesheet
		 * handles the Blink and WebKit side but does nothing in Firefox, so
		 * the attribute and the cancelled event do the rest: `draggable`
		 * stops it being offered, and preventing `dragstart` catches
		 * anything else draggable that ends up in the rail.
		 */
		[...this.rail.querySelectorAll('img')].forEach((img) => {
			img.draggable = false;
		});

		this.rail.addEventListener('dragstart', (e) => e.preventDefault());

		this.rail.addEventListener('pointerdown', (e) => {
			if (e.button !== 0 || e.target.closest('button')) {
				return;
			}

			this.dragging = true;
			this.moved = 0;
			this.startX = e.clientX;
			this.width = this.rail.clientWidth;
			this.el.classList.add('is-dragging');
			this.rail.setPointerCapture(e.pointerId);
		});

		this.rail.addEventListener('pointermove', (e) => {
			if (!this.dragging) {
				return;
			}

			const delta = e.clientX - this.startX;
			this.moved = Math.abs(delta);

			this.rail.style.transform = `translateX(${-this.current * this.width + delta}px)`;

			// The copy follows the gesture rather than waiting for it, so the
			// fade and the travel read as one movement.
			const progress = delta / this.width;
			this.panelEls.forEach((panel, i) => {
				if (panel) {
					panel.style.opacity = String(Math.max(0, 1 - Math.abs(i - this.current - progress)));
				}
			});
		});

		const end = (e) => {
			if (!this.dragging) {
				return;
			}

			this.dragging = false;
			this.el.classList.remove('is-dragging');
			this.panelEls.forEach((panel) => {
				if (panel) {
					panel.style.opacity = '';
				}
			});

			const delta = e.clientX - this.startX;
			const far = Math.abs(delta) > this.width * DRAG_THRESHOLD;

			this.show(far ? this.current - Math.sign(delta) : this.current);

			window.setTimeout(() => {
				this.moved = 0;
			}, 0);
		};

		this.rail.addEventListener('pointerup', end);
		this.rail.addEventListener('pointercancel', end);
	};

	/**
	 * Clamped rather than wrapped, which is what the disabled arrows say at
	 * either end. The resting transform is a percentage so it survives a
	 * resize; only the live drag works in pixels.
	 */
	show = (index, animate = true) => {
		const last = this.slides.length - 1;
		this.current = Math.max(0, Math.min(index, last));

		if (!animate) {
			this.rail.style.transition = 'none';
		}

		this.rail.style.transform = `translateX(${-this.current * 100}%)`;

		if (!animate) {
			// Reading a layout property applies the value before the
			// transition returns, so the jump is not animated.
			this.rail.getBoundingClientRect();
			this.rail.style.transition = '';
		}

		this.panelEls.forEach((panel, i) => {
			if (!panel) {
				return;
			}

			const isCurrent = i === this.current;
			panel.classList.toggle('is-current', isCurrent);
			// Stacked one on top of another, so the ones behind are taken out
			// of the tab order and the accessibility tree by hand.
			panel.inert = !isCurrent;
			panel.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
		});

		[...this.el.querySelectorAll('[data-anatomy-go]')].forEach((dot, d) => {
			dot.classList.toggle('is-current', d === this.current);
			dot.setAttribute('aria-current', d === this.current ? 'true' : 'false');
		});

		const prev = this.el.querySelector('[data-anatomy-prev]');
		const next = this.el.querySelector('[data-anatomy-next]');
		if (prev) {
			prev.disabled = this.current === 0;
		}
		if (next) {
			next.disabled = this.current === last;
		}
	};
}

document.addEventListener('DOMContentLoaded', () => {
	const sliders = [...document.querySelectorAll(`.${NS}`)];
	if (sliders.length) {
		sliders.map((el) => new AnatomySlider(el));
	}
});
